import { ChatType, UserSelectionInput, UserType } from "@src/types.generated";

import { ChatDetailFragment } from "./ChatDetailPage.generated";

export const isUserOnlySelection = (selection: UserSelectionInput): boolean =>
  Boolean(selection.userIds?.length) &&
  !selection.allLocations &&
  !selection.allManagers &&
  !selection.allTrainees &&
  !selection.allAdmins &&
  !selection.locationIds?.length &&
  !selection.locationGroupIds?.length &&
  !selection.roleIds?.length &&
  !selection.roleGroupIds?.length &&
  selection.daysAfterHire == null;

export const isSingleUserSelection = (
  selection: UserSelectionInput,
  viewerId: number | undefined,
): boolean =>
  isUserOnlySelection(selection) &&
  (selection.userIds ?? []).filter((id) => id !== viewerId).length <= 1;

type ChatDisplayMember = {
  id: number;
  name: string;
  firstName: string;
  deactivatedAt?: string | null;
};

type ChatDisplayData = {
  chatType: ChatType;
  userSelectionSet: ChatSelectionSet;
  title?: { en: string } | null;
  members: ChatDisplayMember[];
};

type ChatSelectionSet = {
  userSelections: readonly { userMemberships: readonly unknown[] }[];
};

export const selectionHasUserMemberships = (
  userSelectionSet: ChatSelectionSet,
): boolean =>
  userSelectionSet.userSelections.some(
    (selection) => selection.userMemberships.length > 0,
  );

export const withDeactivatedLabel = (
  name: string,
  member: Pick<ChatDisplayMember, "deactivatedAt">,
): string => (member.deactivatedAt ? `${name} (deactivated)` : name);

export const getChatOtherMembers = <M extends ChatDisplayMember>(
  members: M[],
  viewerId: number | undefined,
): M[] => {
  const others = members.filter((member) => member.id !== viewerId);
  return others.length > 0 ? others : members;
};

export const getChatDisplayTitle = (
  chat: ChatDisplayData,
  viewerId: number | undefined,
): string => {
  if (chat.title?.en) return chat.title.en;
  if (!selectionHasUserMemberships(chat.userSelectionSet)) return "Chat";
  const others = getChatOtherMembers(chat.members, viewerId);
  if (others.length === 0) return "Chat";
  if (others.length === 1) {
    return withDeactivatedLabel(others[0].name, others[0]);
  }
  return others.map((member) => member.firstName).join(", ");
};

export const isOneOnOneChat = (
  chat: Pick<ChatDisplayData, "chatType">,
): boolean => chat.chatType === ChatType.OneOnOne;

const USER_TYPE_LABELS: Record<UserType, string> = {
  [UserType.Admin]: "Admin",
  [UserType.Manager]: "Manager",
  [UserType.Employee]: "Employee",
};

export const getMemberRoleSubtitle = (member: {
  userType: UserType;
  roles: { name: string }[];
  locations: { name: string }[];
}): string => {
  const role = member.roles[0]?.name || USER_TYPE_LABELS[member.userType];
  const location = member.locations[0]?.name;
  return location ? `${role} at ${location}` : role;
};

export type ChatDisplay = {
  title: string;
  subtitle: string | null;
  avatarPerson: ChatDetailFragment["members"][number] | null;
  composerName: string;
};

const oneOnOneSubtitle = (
  member: ChatDetailFragment["members"][number],
): string => getMemberRoleSubtitle(member);

export const getChatDisplay = (
  chat: ChatDetailFragment | null | undefined,
  viewerId: number | undefined,
): ChatDisplay => {
  if (!chat) {
    return { title: "", subtitle: null, avatarPerson: null, composerName: "" };
  }
  const title = getChatDisplayTitle(chat, viewerId);
  if (isOneOnOneChat(chat)) {
    const other = getChatOtherMembers(chat.members, viewerId)[0];
    return {
      title,
      subtitle: oneOnOneSubtitle(other),
      avatarPerson: other,
      composerName: other.firstName,
    };
  }
  return {
    title,
    subtitle: `Created by ${chat.createdBy.name}`,
    avatarPerson: null,
    composerName: title,
  };
};
